import {
  PaymentMethodLiteModel,
  PaymentMethodModel,
  getPaymentMethodKey,
  getPaymentMethodsKey,
  useGetPaymentMethod,
  useGetPaymentMethods,
} from 'api-hooks/payment-method';
import { OnChangeParams, Option } from 'baseui/select';
import { QueryTransformer } from 'common/helpers/common';
import { Input } from 'components/elements';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(
  item: PaymentMethodLiteModel | PaymentMethodModel,
  t: any,
): Option {
  return {
    id: item.id,
    label: t(`common:${item.type}`),
    type: item.type,
    extra: item,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: PaymentMethodModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
}

export default function PaymentMethodSelectInput(props: Props) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<any>();
  const {
    label = t('common:payment_method'),
    placeholder = t('common:select_payment_method'),
  } = props;
  const value = useWatch({ control, name: props.name });

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetPaymentMethods,
    useDetailLazyQueryHook: useGetPaymentMethod,
    listKeyFunction: getPaymentMethodsKey,
    detailKeyFunction: getPaymentMethodKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),
    // renderCreate: !!can(AuthorizationRules.EmployeesCreate),
    // renderCreate: false,
    // onClickCreate: () => {
    //   // navigate(NavigationRoutes.EmployeesCreate);
    //   props.onClose && props.onClose();
    // },
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback(
      (result) => {
        return result.data.map((v) => transformer(v, t));
      },
      [t],
    ),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, PaymentMethodModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data, t);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [t],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      {...selectProps}
    />
  );
}
